import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brandmark } from "@/components/Brand";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Globe, Menu, X, Bell } from "lucide-react";
import InstallPrompt from "@/components/InstallPrompt";
import NotificationSettings from "@/components/NotificationSettings";

export default function PortalShell({ nav = [], active, onNav, children, title, bell }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [showBell, setShowBell] = useState(false);
  const [bellPos, setBellPos] = useState({ top: 60, left: 20 });
  const toggleBell = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setBellPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 262)) });
    setShowBell((s) => !s);
  };
  const [showNotifSettings, setShowNotifSettings] = useState(false);

  const isStaff = ["staff", "admin"].includes(user?.role);
  const doLogout = async () => { await logout(); navigate("/"); };
  const handleNav = (key) => { onNav(key); setMenuOpen(false); };

  const NavBtn = (n) => (
    <button key={n.key} data-testid={`nav-${n.key}`} className={`side-link ${active === n.key ? "active" : ""}`} onClick={() => handleNav(n.key)}>
      {n.icon}<span>{n.label}</span>
      {n.badge > 0 && <span className="nav-badge" data-testid={`nav-badge-${n.key}`}>{n.badge}</span>}
    </button>
  );
  const renderNav = () => {
    if (!nav.some((n) => n.group)) return nav.map(NavBtn);
    const order = []; const map = {};
    nav.forEach((n) => { const g = n.group || "More"; if (!map[g]) { map[g] = []; order.push(g); } map[g].push(n); });
    return order.map((g) => (
      <div key={g} style={{ marginBottom: 10 }}>
        <div className="nav-group-label">{g}</div>
        {map[g].map(NavBtn)}
      </div>
    ));
  };

  const bellUI = bell ? (
    <div style={{ position: "relative" }}>
      <button onClick={toggleBell} data-testid="bell-btn" aria-label="Notifications" style={{ background: "none", border: "none", cursor: "pointer", position: "relative", color: "var(--ink)", display: "flex" }}>
        <Bell size={20} />
        {bell.count > 0 && <span className="nav-badge" data-testid="bell-count" style={{ position: "absolute", top: -7, right: -9, marginLeft: 0 }}>{bell.count}</span>}
      </button>
      {showBell && (
        <>
          <div onClick={() => setShowBell(false)} style={{ position: "fixed", inset: 0, zIndex: 2999 }} />
          <div data-testid="bell-menu" style={{ position: "fixed", top: bellPos.top, left: bellPos.left, width: 250, maxWidth: "80vw", background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 10, boxShadow: "0 14px 34px -12px rgba(0,0,0,.3)", zIndex: 3000, padding: 10 }}>
            <div className="nav-group-label" style={{ padding: "4px 8px 8px" }}>Unread messages</div>
            {(bell.items || []).length === 0 && <div style={{ fontSize: 13, color: "var(--muted)", padding: "6px 8px" }}>You're all caught up.</div>}
            {(bell.items || []).map((it) => (
              <button key={it.pid} data-testid="bell-item" onClick={() => { setShowBell(false); bell.onSelect(it.pid); }} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", background: "none", border: "none", cursor: "pointer", padding: "8px", fontSize: 14, textAlign: "left", borderRadius: 6, color: "var(--ink)" }}>
                <span>{it.name}</span><span className="nav-badge" style={{ marginLeft: 8 }}>{it.count}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  ) : null;

  return (
    <div className="portal-bg portal-shell" style={{ display: "grid", gridTemplateColumns: "260px 1fr" }}>
      <div className="portal-topbar">
        <div style={{ cursor: "pointer" }} onClick={() => navigate("/")}><Brandmark fontSize={17} size={24} /></div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          {bellUI}
          <button className="portal-menu-btn" onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn"><Menu size={18} /> Menu</button>
        </div>
      </div>

      {menuOpen && <div className="portal-overlay" onClick={() => setMenuOpen(false)} />}

      <aside className={`portal-aside ${menuOpen ? "open" : ""}`} style={{ borderRight: "1px solid var(--line)", background: "var(--paper)", padding: "28px 18px", position: "sticky", top: 0, height: "100vh", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingLeft: 6, marginBottom: 34 }}>
          <div style={{ cursor: "pointer" }} onClick={() => navigate("/")}><Brandmark fontSize={20} size={28} /></div>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {bellUI}
            <button className="portal-burger" onClick={() => setMenuOpen(false)} aria-label="Close menu"><X size={20} /></button>
          </div>
        </div>

        <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {renderNav()}
        </nav>

        <div style={{ marginTop: "auto", borderTop: "1px solid var(--line)", paddingTop: 16 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
            <button className="side-link" onClick={() => navigate("/board")} style={{ padding: "8px 16px" }}>
              <Globe size={16} /> Notice board
            </button>
          </div>
          <button className="side-link" data-testid="notif-settings-btn" onClick={() => { setShowNotifSettings(true); setMenuOpen(false); }} style={{ padding: "8px 16px", marginBottom: 12 }}>
            <Bell size={16} /> Notifications
          </button>
          {isStaff && (
            <div style={{ display: "flex", gap: 8, padding: "0 6px 10px" }}>
              <button className="link-u" onClick={() => navigate("/staff")}>Staff</button>
              <button className="link-u" onClick={() => navigate("/portal")}>Portal view</button>
            </div>
          )}
          <div style={{ padding: "0 6px", fontSize: 13, color: "var(--ink)" }}>{user?.name}</div>
          <div style={{ padding: "0 6px 12px", fontSize: 11, color: "var(--muted)", textTransform: "capitalize" }}>{user?.role}</div>
          <button data-testid="logout-btn" className="side-link" onClick={doLogout}><LogOut size={16} /> Sign out</button>
        </div>
      </aside>

      <main style={{ padding: "clamp(20px,4vw,56px)", maxWidth: 1180, minWidth: 0 }}>
        {title && <h1 style={{ fontSize: "clamp(30px,5vw,52px)", marginBottom: 28 }}>{title}</h1>}
        {children}
      </main>
      <InstallPrompt />
      {showNotifSettings && <NotificationSettings role={user?.role} onClose={() => setShowNotifSettings(false)} />}
    </div>
  );
}

export function StatusPill({ status }) {
  const map = {
    "All good": { bg: "#e7ecdf", c: "#5B6440" }, "paid": { bg: "#e7ecdf", c: "#5B6440" },
    "unpaid": { bg: "#f3e4cf", c: "#8a5a1f" }, "open": { bg: "#f3e4cf", c: "#8a5a1f" },
    "in progress": { bg: "#e4e9f0", c: "#3a5170" }, "resolved": { bg: "#e7ecdf", c: "#5B6440" },
    "Action taken": { bg: "#e4e9f0", c: "#3a5170" }, "approved": { bg: "#e7ecdf", c: "#5B6440" },
    "declined": { bg: "#f0dede", c: "#9a3b3b" },
    "scheduled": { bg: "#e4e9f0", c: "#3a5170" }, "completed": { bg: "#e7ecdf", c: "#5B6440" },
    "cancelled": { bg: "#f0dede", c: "#9a3b3b" },
  };
  const s = map[status] || { bg: "var(--sand)", c: "var(--umber)" };
  return <span className="pill-tag" style={{ background: s.bg, color: s.c }}>{status}</span>;
}
